import type { LeadEnrichment } from '../api/types';

/**
 * Engagement rate for a creator, as a percentage.
 *
 * The original formula was `totalLikes / videoCount / followers`, which
 * divides by the follower count. TikTok reach comes from the For You page,
 * not the follower graph, so that breaks on exactly the accounts worth
 * finding: 729 followers and 26k average views reported as 909% engagement.
 *
 * Views are the correct denominator. Two sources, in order:
 *
 *   1. `engagementRate` — median (likes+comments+shares)/views across recent
 *      videos, computed at enrichment time. Accurate and recency-weighted.
 *   2. A fallback derived from stored lifetime totals for creators enriched
 *      before that field existed: average likes ÷ average views. Same shape,
 *      lower fidelity (likes only, lifetime rather than recent), so it's
 *      labelled as approximate in the UI rather than silently blended.
 */
export interface EngagementResult {
  /** Percentage, or null when it can't be measured. */
  value: number | null;
  /** False when derived from lifetime totals rather than recent videos. */
  precise: boolean;
  /** Videos behind a precise figure. */
  sample: number;
}

export function engagement(e: LeadEnrichment | null | undefined): EngagementResult {
  if (!e) return { value: null, precise: false, sample: 0 };

  const measured = e.engagementRate;
  if (typeof measured === 'number' && Number.isFinite(measured)) {
    return { value: measured, precise: true, sample: Number(e.engagementSample) || 0 };
  }

  // Fallback for rows enriched before per-video data was kept.
  const videoCount = Number(e.videoCount) || 0;
  const totalLikes = Number(e.totalLikes) || 0;
  const avgViews = Number(e.avgViews) || 0;
  if (videoCount <= 0 || avgViews <= 0 || totalLikes <= 0) {
    return { value: null, precise: false, sample: 0 };
  }
  const avgLikes = totalLikes / videoCount;
  return { value: (avgLikes / avgViews) * 100, precise: false, sample: 0 };
}

/** Sort key — unmeasurable creators sink rather than sorting as zero. */
export function engagementSortValue(e: LeadEnrichment | null | undefined): number {
  const { value } = engagement(e);
  return value ?? -1;
}

export function formatEngagement(r: EngagementResult): string {
  if (r.value === null) return '—';
  // Rates above ~100% mean the fallback is being applied to an account whose
  // lifetime likes outrun its recent view average. Cap the display so one odd
  // row can't make the column unreadable; the tooltip carries the real number.
  const shown = Math.min(r.value, 999);
  return `${shown.toFixed(1)}%`;
}

export function engagementTooltip(r: EngagementResult): string {
  if (r.value === null) return 'Not enough data to measure engagement.';
  if (r.precise) {
    return `Median of (likes + comments + shares) ÷ views across their last ${r.sample} video${r.sample === 1 ? '' : 's'}.`;
  }
  return `Approximate: average likes ÷ average views over their whole history (${r.value.toFixed(1)}%). Re-run a search to measure this properly from recent videos.`;
}

/**
 * Rough quality bands for TikTok engagement-by-views. Typical content sits
 * around 3-6%; above 10% is genuinely strong.
 */
export function engagementTone(r: EngagementResult): 'strong' | 'ok' | 'weak' | 'none' {
  if (r.value === null) return 'none';
  if (r.value >= 10) return 'strong';
  if (r.value >= 4) return 'ok';
  return 'weak';
}
